import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AccessSource, EmployeeSubtype, EmployeeType } from '@prisma/client';
import { Breadcrumb, Button, Descriptions, Form, Input, Layout, Result, Select, Space, Spin } from 'antd';
import { DefaultOptionType } from 'antd/es/select';

import { Role } from '@app/domain-interfaces';
import { Auth, Hooks, PageLayout, Picker, Tables } from '@app/web-components';

import { NotFoundPage } from '../../../common/pages/not-found';
import { useFindWorkers } from '../../../subscriptions/data/workers';
import { useFindAccessFromRoo } from '../../access/data';
import { useFindDepartmentByCode } from '../../access/data/departments';
import { usePromoteAccessRule } from '../data/access-rule';
import { exceptionUniquenessValidator, FormException } from '../libs';

const PAGE_TITLE = 'Promote Access Rule';

interface AccessRulePromoteFormData {
  owners: DefaultOptionType[];
  exceptions: FormException[];
}

export const PromotePage = () => {
  const create = usePromoteAccessRule();
  const [form] = Form.useForm<AccessRulePromoteFormData>();
  const formLayoutProps = Hooks.useFormLayout();
  const { getSelectProps: getOwnersSelectProps } = Picker.useWorkerPicker({ labelInValue: false, mode: 'multiple' });
  const { getSelectProps: getExceptionUserSelectProps } = Picker.useWorkerPicker();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = Auth.useUserAuth();

  const { data: access, isFetching: isFetchingAccess } = useFindAccessFromRoo(
    searchParams.get('a'),
    searchParams.get('s') as AccessSource,
  );
  const { data: conflictAccess, isFetching: isFetchingConflictAccess } = useFindAccessFromRoo(
    searchParams.get('c'),
    searchParams.get('cs') as AccessSource,
  );
  const { data: department, isFetching: isFetchingDepartment } = useFindDepartmentByCode(searchParams.get('d'));

  const { data: owners, isFetching: isFetchingOwners } = useFindWorkers(searchParams.get('o')?.split(','));

  const exceptions =
    searchParams
      .get('e')
      ?.split(';')
      .map((x) => {
        const [enterpriseId, justification] = x.split(',');
        return { enterpriseId, justification: decodeURIComponent(justification) };
      }) ?? [];
  const { data: exceptionWorkers, isFetching: isFetchingExceptionWorkers } = useFindWorkers(
    exceptions.map(({ enterpriseId }) => enterpriseId),
  );

  if (!user?.roles[Role.RULE_ADMIN]) {
    navigate('/access-forbidden');
  }

  if (
    isFetchingAccess ||
    isFetchingConflictAccess ||
    isFetchingDepartment ||
    isFetchingOwners ||
    isFetchingExceptionWorkers
  ) {
    return <Spin style={{ width: '100%' }} />;
  }

  if (
    access &&
    conflictAccess &&
    access.employeeType !== 'ANY' &&
    conflictAccess &&
    conflictAccess.employeeType !== 'ANY' &&
    access.employeeType !== conflictAccess.employeeType
  ) {
    return (
      <Result
        status="error"
        title={`Promoting ${access.name} and ${conflictAccess.name}`}
        subTitle="The employee types of these two accesses do not match."
      />
    );
  }

  const typeParam = searchParams.get('t')?.toLocaleUpperCase();
  const employeeType =
    (access?.employeeType === 'ANY' ? undefined : access?.employeeType) ??
    (conflictAccess?.employeeType === 'ANY' ? undefined : conflictAccess?.employeeType) ??
    (typeParam === 'B' ? EmployeeType.INTEL : typeParam === 'G' ? EmployeeType.CONTINGENT_WORKER : undefined);
  const subtypeParam = searchParams.get('u') as EmployeeSubtype;
  const employeeSubtype = Object.values(EmployeeSubtype).includes(subtypeParam) ? subtypeParam : undefined;

  if (
    !access ||
    (!conflictAccess && !department) ||
    !employeeType ||
    (employeeType === EmployeeType.CONTINGENT_WORKER && !employeeSubtype)
  ) {
    return <NotFoundPage />;
  }

  const initialValues: AccessRulePromoteFormData = {
    owners: owners?.map((d, index) => ({ id: -index, label: d.name, value: d.enterpriseId })) ?? [],
    exceptions:
      exceptionWorkers?.map((e, index) => ({
        id: -index,
        user: { label: e.name, value: e.enterpriseId },
        justification: exceptions.find((x) => x.enterpriseId === e.enterpriseId)?.justification ?? '',
      })) ?? [],
  };

  if (create.isSuccess) {
    return (
      <Result
        status="success"
        title="Successfully promoted an access rule!"
        subTitle={`Created an access rule for ${access.name}`}
        extra={[
          <Button type="primary" key="go to access" onClick={() => navigate('../')}>
            Go To Manage Rules
          </Button>,
          <Button
            key="create another"
            onClick={() => {
              create.reset();
              form.resetFields();
            }}
          >
            Create Another
          </Button>,
        ]}
      />
    );
  }

  if (create.isError) {
    return (
      <Result
        status="error"
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        title={(create.error.response?.data as any)?.message ?? create.error.message}
        extra={[
          <Button type="primary" key="go to access" onClick={() => navigate('../')}>
            Go To Manage Rules
          </Button>,
          <Button
            key="create another"
            onClick={() => {
              create.reset();
            }}
          >
            Return to Promote Page
          </Button>,
        ]}
      />
    );
  }

  return (
    <>
      <Breadcrumb
        style={{ margin: '16px 0' }}
        items={[
          { title: <Link to="/">Home</Link> },
          { title: <Link to="../">Access Rules</Link> },
          { title: PAGE_TITLE },
        ]}
      />
      <Layout.Content {...formLayoutProps}>
        <PageLayout.Title title={PAGE_TITLE} />
        <Descriptions title="Access Rule Details">
          <Descriptions.Item label="Access">{access?.name}</Descriptions.Item>
          {conflictAccess ? (
            <Descriptions.Item label="Conflict Access">{conflictAccess.name}</Descriptions.Item>
          ) : (
            <Descriptions.Item label="Department">{department?.departmentNm}</Descriptions.Item>
          )}
          <Descriptions.Item label="Employee Type">{employeeType}</Descriptions.Item>
          {employeeType === EmployeeType.CONTINGENT_WORKER && (
            <Descriptions.Item label="Employee Subtype">{employeeSubtype}</Descriptions.Item>
          )}
        </Descriptions>
        <Form
          form={form}
          initialValues={initialValues}
          layout="vertical"
          style={{ padding: '1em' }}
          onFinish={(data) => {
            create.mutate({
              access,
              externalId: conflictAccess?.externalId,
              accessSource: conflictAccess?.accessSource,
              departmentId: department?.departmentCd,
              employeeType,
              employeeSubtype,
              owners: data.owners.map((x) => x.value?.toString() ?? ''),
              exceptions: data.exceptions.map((x) => ({
                wwid: x.user.value?.toString() ?? '',
                justification: x.justification,
              })),
            });
          }}
        >
          <Form.Item label="Owners" name="owners" rules={[{ required: true }]}>
            <Select {...getOwnersSelectProps({ placeholder: 'Search Intel workers to set as owners' })} />
          </Form.Item>
          <Form.Item
            label="Exceptions"
            name="exceptions"
            rules={[
              {
                validator: exceptionUniquenessValidator,
              },
            ]}
          >
            <Tables.DataTable recordType="Exception">
              <Form.Item label="User" name="user" rules={[{ required: true }]}>
                <Select {...getExceptionUserSelectProps({ style: { minWidth: '275px' } })} />
              </Form.Item>
              <Form.Item label="Justification" name="justification" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Tables.DataTable>
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={create.isPending}>
              Save
            </Button>
            <Button onClick={() => form.resetFields()}>Reset</Button>
            <Button onClick={() => navigate(-1)}>Cancel</Button>
          </Space>
        </Form>
      </Layout.Content>
    </>
  );
};
